/**
 * FinancialNumber component — Brand Book §2.9
 * IBM Plex Mono, Belgian formatting (dot thousands, comma decimals)
 * tabular-nums for column alignment
 * Colorize: green for positive, red for negative (invertColor flips this)
 * Size tokens: sm (10px/500), md (13px/600), lg (22px/700)
 */

import { clsx } from 'clsx';

export interface FinancialNumberProps {
  value: number;
  format?: 'currency' | 'percentage' | 'bps' | 'ratio' | 'number';
  showSign?: boolean;
  colorize?: boolean;
  /** When true, negative = success, positive = error (for costs where lower is better) */
  invertColor?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function formatBelgianNumber(value: number, decimals?: number): string {
  const abs = Math.abs(value);

  // Auto-detect decimals if not specified: show fractional part if it exists
  const effectiveDecimals =
    decimals !== undefined
      ? decimals
      : abs % 1 !== 0
        ? 2
        : 0;

  const parts = abs.toFixed(effectiveDecimals).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decPart = parts[1];
  return decPart ? `${intPart},${decPart}` : intPart;
}

export function FinancialNumber({
  value,
  format = 'currency',
  showSign = false,
  colorize = false,
  invertColor = false,
  size = 'md',
  className,
}: FinancialNumberProps) {
  let formatted: string;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const absValue = Math.abs(value);

  switch (format) {
    case 'currency':
      formatted = `€${formatBelgianNumber(absValue)}`;
      break;
    case 'percentage':
      formatted = `${formatBelgianNumber(absValue, 2)}%`;
      break;
    case 'bps':
      formatted = `${formatBelgianNumber(absValue, 0)} bps`;
      break;
    case 'ratio':
      formatted = `${formatBelgianNumber(absValue, 1)}×`;
      break;
    case 'number':
    default:
      formatted = formatBelgianNumber(absValue);
      break;
  }

  if (showSign && value !== 0) {
    formatted = `${sign}${formatted}`;
  } else if (value < 0) {
    formatted = `${sign}${formatted}`;
  }

  const sizeStyles = {
    sm: 'text-mono-sm',
    md: 'text-mono-base',
    lg: 'text-mono-lg',
  };

  const getColorStyle = () => {
    if (!colorize) {return 'text-txt-primary';}
    if (value === 0) {return 'text-txt-primary';}

    const isPositiveGood = invertColor ? value < 0 : value > 0;
    return isPositiveGood ? 'text-success' : 'text-error';
  };

  return (
    <span
      className={clsx(
        'font-mono tabular-nums',
        sizeStyles[size],
        getColorStyle(),
        className
      )}
    >
      {formatted}
    </span>
  );
}